import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Bell, CheckCheck, Trash2, Package, TrendingUp, AlertCircle, Loader2 } from 'lucide-react';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { notificationService, isNotificationRead } from '../services/notificationService';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

interface Notification {
  id: string;
  type: 'offer' | 'bid' | 'approval' | 'rejection' | 'system';
  title: string;
  message: string;
  time: string;
  read: boolean;
  icon?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
}

export function DriverMobileNotifications() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const userId = (getStoredDriverMobileUser<any>() || authUser)?.id;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadNotifications();
  }, [userId]);

  const getNotificationTarget = (notification: Notification) => {
    if (notification.relatedEntityType === 'bid_request') return notification.relatedEntityId ? `/driver-mobile-offer/${notification.relatedEntityId}` : null;
    if (notification.relatedEntityType === 'shipment') return notification.relatedEntityId ? `/driver-mobile-shipment-tracking/${notification.relatedEntityId}` : '/driver-mobile-offers';
    if (notification.relatedEntityType === 'driver-defense') return '/driver-mobile-defenses';
    return null;
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!userId) throw new Error('Sürücü oturumu bulunamadı');
      const response = await notificationService.getByUser(String(userId));
      const data = response.data || response;
      setNotifications(
        (Array.isArray(data) ? data : []).map((item: any) => ({
          id: String(item.id || item.notification_id),
          type: item.type || 'system',
          title: item.title || 'Bildirim',
          message: item.message || item.description || '',
          time: item.created_at
            ? new Date(item.created_at).toLocaleString('tr-TR')
            : item.time || '',
          read: isNotificationRead(item),
          relatedEntityType: item.related_entity_type,
          relatedEntityId: item.related_entity_id ? String(item.related_entity_id) : undefined,
        }))
      );
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError('Bildirimler yuklenemedi');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(current => current.map(n => n.id === id ? { ...n, read: true } : n));
      return true;
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      toast.error('Bildirim okundu olarak kaydedilemedi. Lütfen tekrar deneyin.');
      return false;
    }
  };

  const openNotification = async (notification: Notification) => {
    if (!notification.read && !(await markAsRead(notification.id))) return;
    const target = getNotificationTarget(notification);
    if (target) navigate(target);
  };

  const markAllAsRead = async () => {
    try {
      if (!userId) throw new Error('Sürücü oturumu bulunamadı');
      await notificationService.markAllAsRead(String(userId));
      setNotifications(current => current.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      toast.error('Bildirimler okundu olarak kaydedilemedi.');
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await notificationService.delete(id);
      setNotifications(current => current.filter(n => n.id !== id));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const deleteAllNotifications = async () => {
    try {
      if (!userId) throw new Error('Sürücü oturumu bulunamadı');
      await notificationService.deleteAllByUser(String(userId));
      setNotifications([]);
    } catch (err) {
      console.error('Failed to delete all notifications:', err);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'offer':
        return <Package className="w-6 h-6 text-blue-600" />;
      case 'bid':
        return <TrendingUp className="w-6 h-6 text-orange-600" />;
      case 'approval':
        return <CheckCheck className="w-6 h-6 text-green-600" />;
      case 'rejection':
        return <AlertCircle className="w-6 h-6 text-red-600" />;
      case 'system':
        return <Bell className="w-6 h-6 text-gray-600" />;
      default:
        return <Bell className="w-6 h-6 text-gray-600" />;
    }
  };

  const getColor = (type: string) => {
    switch (type) {
      case 'offer':
        return 'bg-blue-100';
      case 'bid':
        return 'bg-orange-100';
      case 'approval':
        return 'bg-green-100';
      case 'rejection':
        return 'bg-red-100';
      case 'system':
        return 'bg-gray-100';
      default:
        return 'bg-gray-100';
    }
  };

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.read)
    : notifications;

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Bildirimler</h1>
          {notifications.length > 0 ? (
            <div className="flex items-center gap-3">
              {unreadCount > 0 ? (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-blue-700 hover:text-blue-800 dark:text-blue-200 dark:hover:text-white"
                >
                  Tümünü Okundu İşaretle
                </button>
              ) : null}
              <button
                onClick={deleteAllNotifications}
                className="text-sm text-rose-700 hover:text-rose-800 dark:text-rose-200 dark:hover:text-white"
              >
                Tümünü Sil
              </button>
            </div>
          ) : (
            <div className="w-20"></div>
          )}
        </div>

        {/* Stats */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/10 dark:backdrop-blur-sm">
          <p className="text-sm text-slate-600 dark:text-blue-200">Toplam Bildirim</p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{notifications.length}</p>
            {unreadCount > 0 && (
              <p className="text-sm text-slate-600 dark:text-blue-200">• {unreadCount} okunmamış</p>
            )}
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Tümü ({notifications.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              filter === 'unread'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Okunmamış ({unreadCount})
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="px-4 pt-4 space-y-3">
        {loading && (
          <div className="bg-white rounded-xl p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
            <p className="text-gray-500">Bildirimler yukleniyor</p>
          </div>
        )}
        {!loading && error && (
          <div className="bg-white rounded-xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-300 mx-auto mb-3" />
            <p className="text-gray-700 mb-3">{error}</p>
            <button onClick={loadNotifications} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Tekrar dene</button>
          </div>
        )}
        {!loading && !error && filteredNotifications.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {filter === 'unread' ? 'Okunmamış bildirim yok' : 'Henüz bildirim yok'}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white rounded-xl shadow-sm overflow-hidden ${
                !notification.read ? 'border-l-4 border-blue-600' : ''
              }`}
            >
              <div className="p-4">
                <button type="button" onClick={() => void openNotification(notification)} className="flex w-full items-start gap-3 text-left" aria-label={`${notification.title} bildirimini aç`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${getColor(notification.type)}`}>
                    {getIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-bold text-gray-900 text-sm">{notification.title}</h3>
                      {!notification.read && (
                        <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1.5"></span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{notification.message}</p>
                    <p className="text-xs text-gray-400">{notification.time}</p>
                  </div>
                </button>

                {/* Actions */}
                <div className="flex gap-2 mt-3 pt-3 border-t">
                  {!notification.read && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="flex-1 py-2 px-3 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                    >
                      <CheckCheck className="w-4 h-4" />
                      Okundu İşaretle
                    </button>
                  )}
                  <button
                    onClick={() => deleteNotification(notification.id)}
                    className="py-2 px-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center justify-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Sil
                  </button>
                  {getNotificationTarget(notification) && (
                    <button
                      onClick={() => void openNotification(notification)}
                      className="py-2 px-3 bg-blue-50 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                    >
                      Bildirime Git
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom Navigation */}
      <DriverBottomNav />
    </div>
  );
}
